/**
 * CompareImagesModal — the photo stage for the multi-photo layouts. It shows
 * the arrangement the post will use (two tiles for Before & After, a 2x2 grid
 * for Progress Collage) and fills it in place: click a tile to browse, drop
 * files straight onto it, or drag one tile onto another to swap. The composer
 * only accepts a complete set, so Done stays disabled until every tile has a
 * photo (mobile parity: apps/mobile/src/screens/main/createPost/compareImages).
 */
import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useCreatePostData } from '@wlb/shared/context/CreatePostContext';
import {
  POST_IMAGE_LAYOUT,
  POST_IMAGE_LAYOUT_SLOT_COUNT,
  POST_MAX_IMAGE_SIZE_BYTES,
  POST_MEDIA_TYPE,
  SUPPORTED_IMAGE_MIME_TYPES,
  isSupportedImageFormat,
} from '@wlb/shared/constants';
import type { MediaFile } from '@wlb/shared/types';
import type { PostImageLayout } from '@wlb/shared/types/post';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { PencilIcon, PlusIcon, TrashIcon } from '../../components/ui/icons';
import './photoLayout.css';

interface CompareImagesModalProps {
  /** Comparison2 or Comparison4 — decides how many tiles the stage shows. */
  layout: PostImageLayout;
  onClose: () => void;
}

/** Identifies an internal tile drag, so file drops and swaps stay separate. */
const REORDER_MIME = 'application/x-wlb-photo-slot';

const MAX_IMAGE_SIZE_MB = Math.round(POST_MAX_IMAGE_SIZE_BYTES / (1024 * 1024));

const isImageFile = (file: MediaFile | null | undefined): boolean =>
  !!file?.uri &&
  file.mediaType !== POST_MEDIA_TYPE.VIDEO &&
  file.mediaType !== POST_MEDIA_TYPE.GIF;

const resolveSlotCount = (layout: PostImageLayout): number =>
  POST_IMAGE_LAYOUT_SLOT_COUNT[layout as keyof typeof POST_IMAGE_LAYOUT_SLOT_COUNT] ?? 2;

const toMediaFile = (file: File): MediaFile => ({
  uri: URL.createObjectURL(file),
  file,
  fileName: file.name,
  fileSize: file.size,
  type: file.type || 'image/jpeg',
  mediaType: POST_MEDIA_TYPE.IMAGE,
});

export function CompareImagesModal({ layout, onClose }: CompareImagesModalProps) {
  const { t } = useTranslation();
  const { mediaFiles, setCompareImages } = useCreatePostData();
  const slotCount = resolveSlotCount(layout);
  const isBeforeAfter = layout === POST_IMAGE_LAYOUT.COMPARISON_2;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeSlotRef = useRef<number | null>(null);

  // Reopening the picker keeps the album the composer already holds, so the
  // user edits their selection instead of starting over.
  const [slots, setSlots] = useState<(MediaFile | null)[]>(() => {
    const existing = mediaFiles.filter(isImageFile);
    return Array.from({ length: slotCount }, (_, index) =>
      existing.length <= slotCount ? existing[index] ?? null : null,
    );
  });
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const [dragSource, setDragSource] = useState<number | null>(null);
  const [error, setError] = useState('');

  const filledCount = useMemo(() => slots.filter(isImageFile).length, [slots]);
  const allFilled = filledCount === slotCount;

  const slotLabel = (index: number): string => {
    if (isBeforeAfter) {
      return index === 0
        ? t('home.createPost.media.compareBefore')
        : t('home.createPost.media.compareAfter');
    }
    return t('home.createPost.media.compareSlotLabel', { index: index + 1 });
  };

  /**
   * Places files from `startIndex` onward, skipping tiles that are already
   * filled, so dropping three photos on an empty collage fills three tiles
   * instead of overwriting one.
   */
  const placeFiles = (files: File[], startIndex: number) => {
    const images = files.filter(file => file.type.startsWith('image/'));
    if (!images.length) {
      setError(t('home.createPost.media.compareOnlyImages'));
      return;
    }
    if (images.some(file => !isSupportedImageFormat(file.type, file.name))) {
      setError(t('home.createPost.media.invalidImageFormat'));
      return;
    }
    if (images.some(file => file.size > POST_MAX_IMAGE_SIZE_BYTES)) {
      setError(t('home.createPost.media.imageTooLarge', { size: MAX_IMAGE_SIZE_MB }));
      return;
    }
    setError('');
    setSlots(prev => {
      const next = [...prev];
      let cursor = startIndex;
      images.forEach((file, order) => {
        // The first file always lands on the tile the user aimed at, even
        // when it already holds a photo; the rest look for free tiles.
        if (order > 0) {
          while (cursor < next.length && next[cursor]) cursor += 1;
        }
        if (cursor >= next.length) return;
        next[cursor] = toMediaFile(file);
        cursor += 1;
      });
      return next;
    });
  };

  const openPickerForSlot = (index: number) => {
    activeSlotRef.current = index;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const slotIndex = activeSlotRef.current;
    event.target.value = '';
    activeSlotRef.current = null;
    if (!files.length || slotIndex == null) return;
    placeFiles(files, slotIndex);
  };

  const clearSlot = (index: number) => {
    setError('');
    setSlots(prev => prev.map((slot, slotIndex) => (slotIndex === index ? null : slot)));
  };

  const swapSlots = (from: number, to: number) => {
    setSlots(prev => {
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  };

  const handleTileDragStart = (index: number) => (event: DragEvent<HTMLElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(REORDER_MIME, String(index));
    setDragSource(index);
  };

  const handleTileDragOver = (index: number) => (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = dragSource == null ? 'copy' : 'move';
    setDropTarget(index);
  };

  const handleTileDrop = (index: number) => (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDropTarget(null);
    const source = event.dataTransfer.getData(REORDER_MIME);
    setDragSource(null);
    if (source) {
      const from = Number(source);
      if (Number.isInteger(from) && from !== index) swapSlots(from, index);
      return;
    }
    placeFiles(Array.from(event.dataTransfer.files ?? []), index);
  };

  const handleStageDragOver = (event: DragEvent<HTMLElement>) => {
    if (dragSource != null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleStageDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDropTarget(null);
    if (dragSource != null) {
      setDragSource(null);
      return;
    }
    const firstEmpty = slots.findIndex(slot => !isImageFile(slot));
    placeFiles(Array.from(event.dataTransfer.files ?? []), firstEmpty === -1 ? 0 : firstEmpty);
  };

  const handleDone = () => {
    const files = slots.filter((slot): slot is MediaFile => isImageFile(slot));
    if (files.length !== slotCount) return;
    setCompareImages(layout, files);
    onClose();
  };

  return (
    <Modal
      title={t('home.createPost.media.compareTitle')}
      onClose={onClose}
      labelledBy="compare-images-title"
      className="compare-photos-modal"
    >
      <div className="compare-photos">
        <p className="compare-photos__subtitle" id="compare-photos-hint">
          {t('home.createPost.media.compareSubtitle')}
        </p>

        <ul
          className={`compare-photos__stage compare-photos__stage--${isBeforeAfter ? 'two' : 'four'}`}
          onDragOver={handleStageDragOver}
          onDrop={handleStageDrop}
          onDragLeave={() => setDropTarget(null)}
        >
          {slots.map((slot, index) => {
            const isTarget = dropTarget === index;
            const isSource = dragSource === index;
            // Tiles are fixed positions, so the index is a stable key.
            return (
              <li className="compare-photos__slot" key={index}>
                {slot?.uri ? (
                  <div
                    className={`compare-photos__tile compare-photos__tile--filled${isTarget ? ' compare-photos__tile--target' : ''}${isSource ? ' compare-photos__tile--dragging' : ''}`}
                    draggable
                    onDragStart={handleTileDragStart(index)}
                    onDragEnd={() => { setDragSource(null); setDropTarget(null); }}
                    onDragOver={handleTileDragOver(index)}
                    onDrop={handleTileDrop(index)}
                  >
                    <img className="compare-photos__image" src={slot.uri} alt="" />
                    <span className="compare-photos__tag">{slotLabel(index)}</span>
                    <span className="compare-photos__actions">
                      <button
                        type="button"
                        className="compare-photos__action"
                        onClick={() => openPickerForSlot(index)}
                        aria-label={t('home.createPost.media.compareReplace')}
                        title={t('home.createPost.media.compareReplace')}
                      >
                        <PencilIcon size={16} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="compare-photos__action compare-photos__action--danger"
                        onClick={() => clearSlot(index)}
                        aria-label={t('home.createPost.media.compareRemovePhoto')}
                        title={t('home.createPost.media.compareRemovePhoto')}
                      >
                        <TrashIcon size={16} aria-hidden />
                      </button>
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`compare-photos__tile compare-photos__tile--empty${isTarget ? ' compare-photos__tile--target' : ''}`}
                    onClick={() => openPickerForSlot(index)}
                    onDragOver={handleTileDragOver(index)}
                    onDrop={handleTileDrop(index)}
                  >
                    <span className="compare-photos__glyph" aria-hidden="true">
                      <PlusIcon size={18} />
                    </span>
                    <span className="compare-photos__tag compare-photos__tag--empty">
                      {slotLabel(index)}
                    </span>
                    <span className="compare-photos__hint">
                      {t('home.createPost.media.compareDropHint')}
                    </span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <FormError message={error} />

        <footer className="compare-photos__footer">
          <p className="compare-photos__progress" aria-live="polite">
            <span className="compare-photos__count">
              {t('home.createPost.media.compareProgress', { added: filledCount, total: slotCount })}
            </span>
            <span className="compare-photos__reorder-hint">
              {t('home.createPost.media.compareReorderHint')}
            </span>
          </p>
          <Button
            size="lg"
            onClick={handleDone}
            disabled={!allFilled}
            aria-describedby="compare-photos-hint"
          >
            {t('home.createPost.media.compareDone')}
          </Button>
        </footer>

        <input
          ref={fileInputRef}
          className="compare-photos__file-input"
          type="file"
          accept={SUPPORTED_IMAGE_MIME_TYPES.join(',')}
          multiple
          onChange={handleFileChange}
        />
      </div>
    </Modal>
  );
}
